import types from '../mutation-types';
import automationRegistryAPI from '../../api/wgpt/automation_registry';
import { throwErrorMessage } from '../utils/api';

export function dynamicCacheKey(name, params) {
  if (!params || Object.keys(params).length === 0) return name;
  const sorted = Object.keys(params).sort();
  return `${name}:${sorted.map(k => `${k}=${params[k] ?? ''}`).join('&')}`;
}

export const state = {
  triggerRecords: {},
  actionRecords: [],
  dynamicOptionsRecords: [],
  dynamicArrayRecords: [],
  dynamicFlagRecords: [],
  customOptionsRecords: [],
  customObjectRecords: [],
  logicalOperatorRecords: [],
  comparisonOperatorRecords: [],
  templateRecords: null,
  uiFlags: {
    isFetchingTriggers: false,
    isFetchingActions: false,
    isFetchingFields: false,
    isFetchingCustomObjects: false,
    isFetchingLogicalOperators: false,
    isFetchingComparisonOperators: false,
    isFetchingTemplates: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
  },
};

export const getters = {
  getTriggers($state) {
    return $state.triggerRecords;
  },
  getTrigger: $state => triggerName => {
    return $state.triggerRecords[triggerName];
  },
  getActions($state) {
    return $state.actionRecords;
  },
  getAction: $state => actionName => {
    return $state.actionRecords.find(({ action }) => action === actionName);
  },
  getLatestVersion: $state => actionName => {
    const parts = actionName.split('_');
    const versionPart = parts[parts.length - 1];
    const hasVersion = /^v\d+$/.test(versionPart);
    const baseActionName = hasVersion
      ? parts.slice(0, -1).join('_')
      : actionName;

    const actionRecords = $state.actionRecords;
    const actionRegex = new RegExp(`^${baseActionName}(_v\\d+)?$`);
    const actions = actionRecords.filter(({ action }) =>
      actionRegex.test(action)
    );
    const latestVersion = actions.reduce(
      (latest, action) => {
        if (action.version > latest.version) {
          return action;
        }
        return latest;
      },
      { version: 0 }
    );
    return latestVersion;
  },
  getDynamicOptions: $state => key => {
    return $state.dynamicOptionsRecords.find(
      ({ cacheKey }) => cacheKey === key
    );
  },
  getDynamicArrays: $state => key => {
    return $state.dynamicArrayRecords.find(({ cacheKey }) => cacheKey === key);
  },
  getDynamicFlags: $state => key => {
    return $state.dynamicFlagRecords.find(({ cacheKey }) => cacheKey === key);
  },
  getCustomOptions: $state => fieldName => {
    return $state.customOptionsRecords.find(({ name }) => name === fieldName);
  },
  getCustomObjects: $state => objectName => {
    return $state.customObjectRecords.find(({ name }) => name === objectName);
  },
  getLogicalOperators($state) {
    return $state.logicalOperatorRecords;
  },
  getComparisonOperators($state) {
    return $state.comparisonOperatorRecords;
  },
  getTemplates($state) {
    return $state.templateRecords;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  getTriggers: async ({ commit }) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingTriggers: true,
    });
    try {
      const { data } = await automationRegistryAPI.getTriggers();
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_TRIGGERS, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingTriggers: false,
      });
    }
  },
  getDynamicOptions: async ({ commit }, args) => {
    const fieldName = args.fieldName ?? args;
    const params = args.params ?? {};
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingFields: true,
    });
    try {
      const { data } = await automationRegistryAPI.getDynamicOptions(
        fieldName,
        params
      );
      commit(types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_FIELDS, {
        ...data,
        cacheKey: dynamicCacheKey(fieldName, params),
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingFields: false,
      });
    }
  },
  getDynamicArrays: async ({ commit }, args) => {
    const fieldName = args.fieldName ?? args;
    const params = args.params ?? {};
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingFields: true,
    });
    try {
      const { data } = await automationRegistryAPI.getDynamicArrays(
        fieldName,
        params
      );
      commit(types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_DYNAMIC_ARRAYS, {
        ...data,
        cacheKey: dynamicCacheKey(fieldName, params),
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingFields: false,
      });
    }
  },
  getDynamicFlags: async ({ commit }, args) => {
    const flagName = args.flagName ?? args;
    const params = args.params ?? {};
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingFields: true,
    });
    try {
      const { data } = await automationRegistryAPI.getDynamicFlags(
        flagName,
        params
      );
      commit(types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_DYNAMIC_FLAGS, {
        ...data,
        cacheKey: dynamicCacheKey(flagName, params),
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingFields: false,
      });
    }
  },
  getCustomOptions: async ({ commit }, fieldName) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingFields: true,
    });
    try {
      const { data } = await automationRegistryAPI.getCustomOptions(fieldName);
      commit(types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_CUSTOM_OPTIONS, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingFields: false,
      });
    }
  },
  getCustomObjects: async (
    { commit, getters: moduleGetters, dispatch },
    objectName
  ) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingCustomObjects: true,
    });
    try {
      const { data } = await automationRegistryAPI.getCustomObjects(objectName);
      commit(types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_CUSTOM_OBJECTS, data);

      // A custom object field can itself be a wrapper — referencing another
      // custom object (e.g. car_listing.make -> car_make) or a dynamic
      // options model (e.g. contact.custom_attributes). Fetch those too so
      // nested fields resolve everywhere a custom object is consumed.
      // Already stored records are skipped, which also breaks reference
      // cycles.
      const wrapperOptions = (data.options || []).filter(
        option => option.wrapper
      );
      wrapperOptions
        .filter(option => option.custom_object)
        .filter(option => !moduleGetters.getCustomObjects(option.custom_object))
        .forEach(option => {
          dispatch('getCustomObjects', option.custom_object);
        });
      wrapperOptions
        .filter(option => option.model)
        .filter(option => !moduleGetters.getDynamicOptions(option.model))
        .forEach(option => {
          dispatch('getDynamicOptions', option.model);
        });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingCustomObjects: false,
      });
    }
  },
  getActions: async ({ commit }, triggerId) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingActions: true,
    });
    try {
      const { data } = await automationRegistryAPI.getActions(triggerId);
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_ACTIONS, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingActions: false,
      });
    }
  },
  getLogicalOperators: async ({ commit }) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingLogicalOperators: true,
    });
    try {
      const { data } = await automationRegistryAPI.getLogicalOperators();
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_LOGICAL_OPERATORS, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingActions: false,
      });
    }
  },
  getComparisonOperators: async ({ commit }) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingComparisonOperators: true,
    });
    try {
      const { data } = await automationRegistryAPI.getComparisonOperators();
      commit(
        types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_COMPARISON_OPERATORS,
        data
      );
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingActions: false,
      });
    }
  },
  getTemplates: async ({ commit }) => {
    commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
      isFetchingTemplates: true,
    });
    try {
      const { data } = await automationRegistryAPI.getTemplates();
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_TEMPLATES, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG, {
        isFetchingTemplates: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_TRIGGERS]($state, data) {
    $state.triggerRecords = data;
  },
  [types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_ACTIONS]($state, data) {
    $state.actionRecords = data;
  },
  [types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_LOGICAL_OPERATORS]($state, data) {
    $state.logicalOperatorRecords = data;
  },
  [types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_COMPARISON_OPERATORS](
    $state,
    data
  ) {
    $state.comparisonOperatorRecords = data;
  },
  [types.SET_WGPT_BOARDS_AUTOMATION_REGISTRY_TEMPLATES]($state, data) {
    $state.templateRecords = data;
  },
  [types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_FIELDS]($state, data) {
    const index = $state.dynamicOptionsRecords.findIndex(
      ({ cacheKey }) => cacheKey === data.cacheKey
    );
    if (index === -1) {
      $state.dynamicOptionsRecords.unshift(data);
    } else {
      const dataBefore = $state.dynamicOptionsRecords[index];
      $state.dynamicOptionsRecords[index] = { ...dataBefore, ...data };
    }
  },
  [types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_DYNAMIC_ARRAYS]($state, data) {
    const index = $state.dynamicArrayRecords.findIndex(
      ({ cacheKey }) => cacheKey === data.cacheKey
    );
    if (index === -1) {
      $state.dynamicArrayRecords.unshift(data);
    } else {
      const dataBefore = $state.dynamicArrayRecords[index];
      $state.dynamicArrayRecords[index] = { ...dataBefore, ...data };
    }
  },
  [types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_DYNAMIC_FLAGS]($state, data) {
    const index = $state.dynamicFlagRecords.findIndex(
      ({ cacheKey }) => cacheKey === data.cacheKey
    );
    if (index === -1) {
      $state.dynamicFlagRecords.unshift(data);
    } else {
      const dataBefore = $state.dynamicFlagRecords[index];
      $state.dynamicFlagRecords[index] = { ...dataBefore, ...data };
    }
  },
  [types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_CUSTOM_OPTIONS]($state, data) {
    const index = $state.customOptionsRecords.findIndex(
      ({ name }) => name === data.name
    );
    if (index === -1) {
      $state.customOptionsRecords.unshift(data);
    } else {
      const dataBefore = $state.customOptionsRecords[index];
      $state.customOptionsRecords[index] = { ...dataBefore, ...data };
    }
  },
  [types.ADD_WGPT_BOARDS_AUTOMATION_REGISTRY_CUSTOM_OBJECTS]($state, data) {
    const index = $state.customObjectRecords.findIndex(
      ({ name }) => name === data.name
    );
    if (index === -1) {
      $state.customObjectRecords.unshift(data);
    } else {
      const dataBefore = $state.customObjectRecords[index];
      $state.customObjectRecords[index] = { ...dataBefore, ...data };
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
